/**
 * NextThreeKickoffs — three-up card row on the landing page showing the top-3
 * nearest upcoming fixtures with LIVE per-second countdowns. Sits between the
 * hero and the general marquee ticker to turn ambient interest into urgency.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "../lib/api";
import { Clock, SoccerBall, Lightning } from "@phosphor-icons/react";
import { teamShort } from "../lib/teamMeta";

function _pad(n) {
  return String(Math.max(0, Math.floor(n))).padStart(2, "0");
}

function _remaining(kickoffIso, nowMs) {
  if (!kickoffIso) return null;
  const target = new Date(kickoffIso).getTime();
  if (Number.isNaN(target)) return null;
  const diff = target - nowMs;
  if (diff <= 0) return { live: true };
  const total = Math.floor(diff / 1000);
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  return { live: false, days, hours, minutes, seconds, totalMs: diff };
}

function _urgencyClass(remaining) {
  if (!remaining || remaining.live) return "border-red-400/60 bg-red-500/5";
  if (remaining.days > 0) return "border-white/10";
  if (remaining.hours >= 6) return "border-primary-yellow/25";
  return "border-primary-yellow/60 bg-primary-yellow/5";
}

export default function NextThreeKickoffs() {
  const [fixtures, setFixtures] = useState([]);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get("/public/upcoming-fixtures", { params: { limit: 3 } })
      .then(({ data }) => {
        if (!cancelled) setFixtures(data.upcoming || []);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const items = useMemo(
    () => (fixtures || []).slice(0, 3).map((f) => ({
      ...f,
      remaining: _remaining(f.kickoff, now),
    })),
    [fixtures, now]
  );

  if (!items.length) return null;

  return (
    <section
      className="border-b border-white/10 surface"
      data-testid="next-three-kickoffs"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-white/50 font-bold">
            <Lightning size={12} weight="fill" className="text-primary-yellow" />
            Kickoff Countdown · Next Up
          </div>
          <Link
            to="/get-started"
            className="hidden sm:inline text-[10px] uppercase tracking-[0.22em] text-primary-yellow font-bold hover:underline"
            data-testid="next-three-cta"
          >
            Pick before whistle →
          </Link>
        </div>
        <div className="grid gap-2 sm:gap-3 sm:grid-cols-3">
          {items.map((f, i) => (
            <FixtureTile key={f.id || i} fixture={f} idx={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function FixtureTile({ fixture, idx }) {
  const r = fixture.remaining;
  const home = teamShort(fixture.home_team) || fixture.home_team;
  const away = teamShort(fixture.away_team) || fixture.away_team;
  return (
    <div
      className={`border ${_urgencyClass(r)} p-3 sm:p-4 flex items-center justify-between gap-3 transition-colors`}
      data-testid={`next-kickoff-${idx}`}
    >
      <div className="min-w-0 flex-1">
        <div className="text-[9px] uppercase tracking-widest text-white/40 flex items-center gap-1">
          <SoccerBall size={9} weight="fill" />
          {fixture.matchweek ? `MW ${fixture.matchweek}` : "Fixture"}
        </div>
        <div className="font-heading text-sm sm:text-base font-black uppercase truncate mt-1 text-white">
          {home} <span className="text-white/40 font-normal text-xs mx-1">vs</span> {away}
        </div>
      </div>
      <div className="shrink-0 text-right">
        {!r || r.live ? (
          <span
            className="text-red-400 font-bold uppercase tracking-widest text-[10px] animate-pulse inline-flex items-center gap-1"
            data-testid={`next-kickoff-live-${idx}`}
          >
            ● Live
          </span>
        ) : (
          <div
            className="font-mono-num tabular-nums text-primary-yellow font-black text-sm sm:text-base leading-none flex items-center gap-1"
            data-testid={`next-kickoff-countdown-${idx}`}
          >
            <Clock size={10} weight="fill" className="text-white/40" />
            {r.days > 0 ? (
              <>
                {r.days}d {_pad(r.hours)}h {_pad(r.minutes)}m
              </>
            ) : (
              <>
                {_pad(r.hours)}:{_pad(r.minutes)}:{_pad(r.seconds)}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
